import React from 'react';
import { useTranslation } from 'react-i18next';

import { publish, SubscriptionTopic } from '../../../../../../../viewer/messaging/pubsub';
import { DesignTool } from '../../../../../../../viewer/tools/design-tool';
import { useSubscribe } from '../../../../../../hooks/use-subscribe';
import { CollapsableSection } from '../../../components/collapsable-section';
import { OptionLabels } from '../../../components/option-labels';
import { CreateOffset } from '../features/create-offset';
import { CreatePoints } from '../features/create-points';
import { GenerateGrid } from '../features/generate-grid';
import { GenerateLineSegments } from '../features/generate-line-segments';
import { ImportGeometry } from '../features/import-geometry';
import { InterpolatePointHeights } from '../features/interpolate-point-heights';
import { SetPointHeights } from '../features/set-point-heights';
import { Triangulate } from '../features/triangulate';
import { DesignFeature } from './design-feature';

export { DesignFeature };

type Props = {
  expanded: boolean;
  tool: DesignTool;
};

export const DesignFeatureSection = (props: Props) => {
  const { t } = useTranslation();
  const [feature, setFeature] = React.useState(props.tool.activeFeature);

  useSubscribe(SubscriptionTopic.DesignToolActiveFeatureChanged, () => {
    setFeature(props.tool.activeFeature);
  });

  return (
    <CollapsableSection
      expanded={props.expanded}
      title={t('section.functions.title', { ns: 'skyviewDesign' }).toUpperCase()}
      onSetExpanded={(state) =>
        publish(SubscriptionTopic.DesignToolMenuStateChanged, { features: state })
      }
    >
      <OptionLabels
        active={feature}
        itemRows={[
          [
            {
              title: t('section.functions.height.text', { ns: 'skyviewDesign' }),
              value: DesignFeature.SetPointHeights,
              tooltipText: t('section.functions.height.tooltip', { ns: 'skyviewDesign' }),
              onSelect: () => setFeature(DesignFeature.SetPointHeights),
              content: <SetPointHeights tool={props.tool} />,
            },
            {
              title: t('section.functions.triangulate.text', { ns: 'skyviewDesign' }),
              value: DesignFeature.Triangulate,
              tooltipText: t('section.functions.triangulate.tooltip', { ns: 'skyviewDesign' }),
              onSelect: () => setFeature(DesignFeature.Triangulate),
              content: <Triangulate tool={props.tool} />,
            },
          ],
          [
            {
              title: t('section.functions.offset.text', { ns: 'skyviewDesign' }),
              value: DesignFeature.CreateOffset,
              tooltipText: t('section.functions.offset.tooltip', { ns: 'skyviewDesign' }),
              onSelect: () => setFeature(DesignFeature.CreateOffset),
              content: <CreateOffset tool={props.tool} />,
            },
            {
              title: t('section.functions.grid.text', { ns: 'skyviewDesign' }),
              value: DesignFeature.GenerateGrid,
              tooltipText: t('section.functions.grid.tooltip', { ns: 'skyviewDesign' }),
              onSelect: () => setFeature(DesignFeature.GenerateGrid),
              content: <GenerateGrid tool={props.tool} />,
            },
          ],
          [
            {
              title: t('section.functions.lineSegment.text', { ns: 'skyviewDesign' }),
              value: DesignFeature.GenerateLineSegments,
              tooltipText: t('section.functions.lineSegment.tooltip', { ns: 'skyviewDesign' }),
              onSelect: () => setFeature(DesignFeature.GenerateLineSegments),
              content: <GenerateLineSegments tool={props.tool} />,
            },
          ],
          [
            {
              title: t('section.functions.interpolatePointHeights.text', { ns: 'skyviewDesign' }),
              value: DesignFeature.InterpolatePointHeights,
              tooltipText: t('section.functions.interpolatePointHeights.tooltip', {
                ns: 'skyviewDesign',
              }),
              onSelect: () => setFeature(DesignFeature.InterpolatePointHeights),
              content: <InterpolatePointHeights tool={props.tool} />,
            },
          ],
          [
            {
              title: t('section.functions.createPoints.text', { ns: 'skyviewDesign' }),
              value: DesignFeature.CreatePoints,
              tooltipText: t('section.functions.createPoints.tooltip', {
                ns: 'skyviewDesign',
              }),
              onSelect: () => setFeature(DesignFeature.CreatePoints),
              content: <CreatePoints tool={props.tool} />,
            },
          ],
          [
            {
              title: t('section.functions.import.text', { ns: 'skyviewDesign' }),
              value: DesignFeature.Import,
              tooltipText: t('section.functions.import.tooltip', { ns: 'skyviewDesign' }),
              onSelect: () => setFeature(DesignFeature.Import),
              content: <ImportGeometry tool={props.tool} />,
            },
          ],
        ]}
      />
    </CollapsableSection>
  );
};
